import { mapActions, mapGetters } from "vuex";
import UnitsService from "@/services/server/UnitsService";
import ConsumptionService from "@/services/server/ConsumptionService";
import ConsumptionBarChart from "@/components/charts/ConsumptionBarChart.vue";
import { showErrorSnackBar } from "@/utils/SnackBartHelper";
import { RoutesPath } from "@/router/paths";

const MONTH_LABELS = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

const BASELINE_TABLE_CALCULATION_KEYS = [
  "realEconomy",
  "savingTotal",
  "baselineConsumptionPerDriver",
  "historicalConsumptionPerDriver",
  "monetaryReferencePerDriver",
  "adjustedMonetaryReference",
];

export default {
  name: "GoalsForecasts",

  components: {
    ConsumptionBarChart,
  },

  data: () => ({
    loadingGoal: false,
    loadingTimeseriesCurrent: false,
    loadingConsumption: false,
    latestRequestId: 0,
    goalCalculation: null,
    goalTimeseriesCurrent: null,
    currentBaselineConfig: null,
    consumptionSeries: [],
    errorMessage: "",
    driverActualValue: "",
    metricViewMode: "total",
    localDateFrom: "",
    localDateTo: "",
    dateFromMenu: false,
    dateToMenu: false,
    isInitializingPeriod: false,
  }),

  computed: {
    ...mapGetters([
      "getSelectedUnit",
      "getSelectedUnits",
      "unitMeasure",
      "getCurrentUserRank",
    ]),

    selectedUnitsList() {
      return Array.isArray(this.getSelectedUnits) ? this.getSelectedUnits : [];
    },

    selectedUnit() {
      if (this.selectedUnitsList.length > 0) {
        return this.normalizeUnit(this.selectedUnitsList[0]);
      }

      return this.normalizeUnit(this.getSelectedUnit);
    },

    selectedUnitName() {
      return this.selectedUnit && (this.selectedUnit.title || this.selectedUnit.name)
        ? this.selectedUnit.title || this.selectedUnit.name
        : "Nenhuma unidade selecionada";
    },

    dateRangePayload() {
      return {
        dateFrom: this.monthInputToPayloadDate(this.localDateFrom, "start"),
        dateTo: this.monthInputToPayloadDate(this.localDateTo, "end"),
      };
    },

    referenceDatePayload() {
      return this.dateRangePayload.dateFrom || this.dateRangePayload.dateTo || "";
    },

    chartGranularity() {
      return "month";
    },

    unitMeasureVuex() {
      return this.unitMeasure || "m3";
    },

    volumeSuffix() {
      return this.unitMeasureVuex === "liters" ? "L" : "m³";
    },

    baselineMinDate() {
      return this.currentBaselineConfig ? this.toMonthInputValue(this.currentBaselineConfig.validFrom) : null;
    },

    baselineMaxDate() {
      return this.currentBaselineConfig && this.currentBaselineConfig.validTo
        ? this.toMonthInputValue(this.currentBaselineConfig.validTo)
        : null;
    },

    dateFromMax() {
      if (this.localDateTo && this.baselineMaxDate) {
        return this.localDateTo < this.baselineMaxDate
          ? this.localDateTo
          : this.baselineMaxDate;
      }

      return this.localDateTo || this.baselineMaxDate;
    },

    dateToMin() {
      if (this.localDateFrom && this.baselineMinDate) {
        return this.localDateFrom > this.baselineMinDate
          ? this.localDateFrom
          : this.baselineMinDate;
      }

      return this.localDateFrom || this.baselineMinDate;
    },

    canLoad() {
      return Boolean(
        this.selectedUnit &&
        this.selectedUnit.id &&
        this.dateRangePayload.dateFrom &&
        this.dateRangePayload.dateTo &&
        !this.hasMultipleUnitsSelected
      );
    },

    hasMultipleUnitsSelected() {
      return this.selectedUnitsList.length > 1;
    },

    isLoadingAny() {
      return this.loadingGoal
        || this.loadingTimeseriesCurrent
        || this.loadingConsumption;
    },

    headerDescription() {
      if (this.hasMultipleUnitsSelected) return "Selecione apenas uma unidade para analisar metas.";
      if (!this.selectedUnit || !this.selectedUnit.id) return "Selecione uma unidade na barra lateral direita.";
      if (!this.dateRangePayload.dateFrom || !this.dateRangePayload.dateTo) return "Selecione um período válido na tela.";
      return "A meta usa a baseline atual válida para o período selecionado.";
    },

    emptyStateTitle() {
      if (this.errorMessage) return "Não foi possível carregar as metas";
      return "Aguardando consulta";
    },

    emptyStateDescription() {
      if (this.hasMultipleUnitsSelected) return "A página de metas aceita apenas uma unidade por vez.";
      if (!this.selectedUnit || !this.selectedUnit.id) return "Use a barra lateral direita para selecionar uma unidade.";
      if (!this.dateRangePayload.dateFrom || !this.dateRangePayload.dateTo) return "Aguardando baseline atual para preencher o período.";
      return "Clique em atualizar para calcular metas e histórico da unidade.";
    },

    hasMissingBaselineState() {
      return Boolean(
        this.selectedUnit
        && this.selectedUnit.id
        && !this.hasMultipleUnitsSelected
        && !this.currentBaselineConfig
        && this.errorMessage
        && this.errorMessage.toLowerCase().includes("baseline")
      );
    },

    baselineSetupRoute() {
      if (!this.selectedUnit || !this.selectedUnit.id) {
        return { path: RoutesPath.UNITS };
      }

      return {
        path: RoutesPath.UNITS,
        query: {
          unitId: this.selectedUnit.id,
          tab: "meta",
          baselineAction: "create",
        },
      };
    },

    canConfigureBaseline() {
      return this.getCurrentUserRank === "Super Admin" || this.getCurrentUserRank === "Admin";
    },

    missingBaselineTitle() {
      return this.canConfigureBaseline
        ? "Esta unidade ainda não possui baseline configurada"
        : "Baseline da unidade ainda não definida";
    },

    missingBaselineDescription() {
      if (this.canConfigureBaseline) {
        return "Para calcular metas, abra a unidade, acesse a aba META e cadastre uma nova baseline com vigência, modo de referência e valores de água/efluentes.";
      }

      return "Entre em contato com a gestão da Foz Sustentável para que a baseline da sua unidade possa ser definida.";
    },

    driverUnitLabel() {
      const currentDriver = this.goalCalculation && this.goalCalculation.driver ? this.goalCalculation.driver : null;
      return currentDriver && currentDriver.unitLabel ? currentDriver.unitLabel : "";
    },

    hasDriverActualValue() {
      return this.driverActualValue !== null
        && this.driverActualValue !== undefined
        && String(this.driverActualValue).trim() !== "";
    },

    currentTimeseriesPoints() {
      const response = this.goalTimeseriesCurrent || {};
      return Array.isArray(response.points) ? response.points : [];
    },

    mergedCurrentTimeline() {
      return this.mergeTimeline(this.currentTimeseriesPoints, this.consumptionSeries);
    },

    mergedActiveTimeline() {
      return this.mergedCurrentTimeline;
    },

    activeSummary() {
      const fallbackReference = this.goalCalculation && this.goalCalculation.referencia
        ? this.goalCalculation.referencia
        : null;
      return this.summarizeTimeline(this.mergedActiveTimeline, fallbackReference);
    },

    currentProjectionSummary() {
      return this.summarizeTimeline(this.mergedCurrentTimeline, this.goalCalculation ? this.goalCalculation.referencia : null);
    },

    baselineComparisonGroups() {
      return this.buildBaselineComparisonGroups(this.mergedCurrentTimeline);
    },

    consumptionChartPayload() {
      return this.buildConsumptionChartPayload(this.mergedActiveTimeline);
    },

    goalChartPayload() {
      return this.buildGoalChartPayload(this.mergedActiveTimeline);
    },

    combinedChartPayload() {
      return this.buildCombinedChartPayload(this.mergedActiveTimeline);
    },

    currentReference() {
      if (this.goalCalculation && this.goalCalculation.referencia) {
        return this.goalCalculation.referencia;
      }

      const response = this.goalTimeseriesCurrent || {};
      const comparisons = Array.isArray(response.baselineComparisons) ? response.baselineComparisons : [];
      if (comparisons.length > 0) {
        return comparisons[0];
      }

      return this.activeSummary.reference || null;
    },

    activeReferenceModeLabel() {
      const reference = this.currentReference || {};
      const labels = {
        fixedBaseline: "Baseline fixa",
        monthlyBaseline: "Baseline mensal",
      };
      return labels[reference.mode] || reference.mode || "-";
    },

    baselineModeTooltip() {
      if (this.isMonthlyBaselineReference) {
        return "Baseline mensal: a referência muda conforme a configuração de cada mês. No modo histórico, cada ponto usa a baseline válida naquela data e, dentro do mês, usa os valores mensais configurados.";
      }

      if (this.isFixedBaselineReference) {
        return "Baseline fixa: a referência é mensal e permanece constante durante a vigência. Em visões diárias ou horárias, a meta é distribuída proporcionalmente pelos dias do mês.";
      }

      return "Modo de baseline usado para calcular a meta no período selecionado.";
    },

    isFixedBaselineReference() {
      return this.currentReference && this.currentReference.mode === "fixedBaseline";
    },

    isMonthlyBaselineReference() {
      return this.currentReference && this.currentReference.mode === "monthlyBaseline";
    },

    hasDriverReferenceColumn() {
      const driver = this.currentBaselineConfig && this.currentBaselineConfig.driver
        ? this.currentBaselineConfig.driver
        : null;

      if (driver && driver.isEnabled) return true;

      return this.baselineTableRows.some((row) => row.driverReference !== null && row.driverReference !== undefined);
    },

    hasHistoricalDriverColumn() {
      return this.baselineTableRows.some((row) => row.historicDriverValue !== null && row.historicDriverValue !== undefined);
    },

    hasDriverComparisonColumns() {
      return this.hasDriverReferenceColumn || this.hasHistoricalDriverColumn;
    },

    driverComparisonAverageKpi() {
      if (!this.hasDriverComparisonColumns) return null;

      const rows = this.baselineTableRows.filter((row) => !row.isEstimative);
      const comparisonRows = rows.filter((row) => row.historicalConsumptionPerDriver !== null && row.historicalConsumptionPerDriver !== undefined);
      if (!comparisonRows.length) return null;

      const baselineRows = comparisonRows.filter((row) => row.baselineConsumptionPerDriver !== null && row.baselineConsumptionPerDriver !== undefined);
      const historicalAverage = Number((
        comparisonRows.reduce((total, row) => total + Number(row.historicalConsumptionPerDriver || 0), 0)
        / comparisonRows.length
      ).toFixed(4));
      const baselineAverage = baselineRows.length
        ? Number((
          baselineRows.reduce((total, row) => total + Number(row.baselineConsumptionPerDriver || 0), 0)
          / baselineRows.length
        ).toFixed(4))
        : null;
      const averageDelta = baselineAverage !== null
        ? Number((historicalAverage - baselineAverage).toFixed(4))
        : null;
      const unitLabel = comparisonRows.find((row) => row.driverUnitLabel)?.driverUnitLabel || this.driverUnitLabel || "";

      return {
        historicalAverage,
        baselineAverage,
        averageDelta,
        monthsCount: comparisonRows.length,
        driverUnitLabel: unitLabel,
      };
    },

    financialDriverComparisonAverageKpi() {
      if (!this.hasDriverComparisonColumns) return null;

      const rows = this.baselineTableRows.filter((row) => !row.isEstimative);
      const comparisonRows = rows
        .map((row) => {
          const historicDriverValue = Number(row.historicDriverValue);
          const historicCost = Number(row.historicCost);
          const actualFinancialPerDriver = Number.isFinite(historicDriverValue)
            && historicDriverValue > 0
            && Number.isFinite(historicCost)
            ? Number((historicCost / historicDriverValue).toFixed(4))
            : null;

          return {
            ...row,
            actualFinancialPerDriver,
          };
        })
        .filter((row) => row.actualFinancialPerDriver !== null);

      if (!comparisonRows.length) return null;

      const baselineRows = comparisonRows.filter((row) => row.monetaryReferencePerDriver !== null && row.monetaryReferencePerDriver !== undefined);
      const actualAverage = Number((
        comparisonRows.reduce((total, row) => total + Number(row.actualFinancialPerDriver || 0), 0)
        / comparisonRows.length
      ).toFixed(4));
      const baselineAverage = baselineRows.length
        ? Number((
          baselineRows.reduce((total, row) => total + Number(row.monetaryReferencePerDriver || 0), 0)
          / baselineRows.length
        ).toFixed(4))
        : null;
      const averageDelta = baselineAverage !== null
        ? Number((actualAverage - baselineAverage).toFixed(4))
        : null;
      const unitLabel = comparisonRows.find((row) => row.driverUnitLabel)?.driverUnitLabel || this.driverUnitLabel || "";

      return {
        actualAverage,
        baselineAverage,
        averageDelta,
        monthsCount: comparisonRows.length,
        driverUnitLabel: unitLabel,
      };
    },

    totalFinancialEconomyKpi() {
      const rows = this.baselineTableRows.filter((row) => !row.isEstimative
        && row.realEconomy !== null
        && row.realEconomy !== undefined);

      if (!rows.length) return null;

      return {
        total: Number(rows.reduce((sum, row) => sum + Number(row.realEconomy || 0), 0).toFixed(2)),
        monthsCount: rows.length,
      };
    },

    isDistributedFixedBaselineView() {
      return this.isFixedBaselineReference && (this.chartGranularity === "day" || this.chartGranularity === "hour");
    },

    graphDescription() {
      return "Consumo real comparado com a baseline atual projetada para o período selecionado.";
    },

    consumptionGraphDescription() {
      return "Consumo real agregado da unidade no período selecionado.";
    },

    goalGraphDescription() {
      if (this.isMonthlyBaselineReference) {
        return "Meta diária calculada a partir da baseline mensal de referência do período selecionado.";
      }

      return "Meta diária média projetada com base na baseline fixa de referência do período selecionado.";
    },

    goalChartTitle() {
      if (this.chartGranularity === "month") {
        return "Meta mensal";
      }

      if (this.chartGranularity === "hour") {
        return this.isFixedBaselineReference ? "Meta horária média" : "Meta horária";
      }

      return this.isFixedBaselineReference ? "Meta diária média" : "Meta diária";
    },

    consumptionGraphTooltip() {
      return "Mostra o consumo real medido da unidade em cada ponto do período selecionado. Este gráfico não aplica meta nem rateio.";
    },

    goalGraphTooltip() {
      if (this.isMonthlyBaselineReference) {
        return "Gráfico único: barras mostram consumo real e linhas mostram a meta. Na baseline mensal, cada ponto usa os valores configurados para o mês correspondente.";
      }

      return "Gráfico único: barras mostram consumo real e linhas mostram a meta. Na baseline fixa, a meta mensal é aplicada ao período selecionado.";
    },

    baselineTableRows() {
      const grouped = {};
      const timeline = Array.isArray(this.mergedActiveTimeline) ? this.mergedActiveTimeline : [];

      timeline.forEach((item) => {
        const referenceDate = item && (item.date || item.referenceDate) ? new Date(item.date || item.referenceDate) : null;
        if (!referenceDate || Number.isNaN(referenceDate.getTime())) return;

        const monthIndex = referenceDate.getUTCMonth();
        const monthNumber = String(monthIndex + 1);
        const year = referenceDate.getUTCFullYear();
        const monthKey = `${year}-${String(monthIndex + 1).padStart(2, "0")}`;

        if (!grouped[monthKey]) {
          grouped[monthKey] = {
            month: monthNumber,
            monthKey,
            label: `${MONTH_LABELS[monthIndex]}/${year}`,
            consumptionWater: 0,
            consumptionSewer: 0,
            consumptionTotal: 0,
            goalWater: 0,
            goalSewer: 0,
            goalTotal: 0,
            baselineWater: null,
            baselineSewer: null,
            baselineTotal: null,
            historicCost: null,
            historicDriverValue: null,
            savingWater: null,
            savingSewer: null,
            savingTotal: null,
            monetaryReference: null,
            driverReference: null,
            driverUnitLabel: "",
            baselineConsumptionPerDriver: null,
            historicalConsumptionPerDriver: null,
            monetaryReferencePerDriver: null,
            adjustedMonetaryReference: null,
            realEconomy: null,
            metaEconomiaPercentual: null,
            pointsCount: 0,
            hasValues: false,
            isEstimative: false,
          };
        }

        const row = grouped[monthKey];
        if (item.isEstimative) {
          row.isEstimative = true;
        }

        if (item.consumptionWater !== null && item.consumptionWater !== undefined) {
          row.consumptionWater += Number(item.consumptionWater || 0);
          row.hasValues = true;
        }

        if (item.consumptionSewer !== null && item.consumptionSewer !== undefined) {
          row.consumptionSewer += Number(item.consumptionSewer || 0);
          row.hasValues = true;
        }

        if (item.metaAgua !== null && item.metaAgua !== undefined) {
          row.goalWater += Number(item.metaAgua || 0);
          row.hasValues = true;
        }

        if (item.metaEsgoto !== null && item.metaEsgoto !== undefined) {
          row.goalSewer += Number(item.metaEsgoto || 0);
          row.hasValues = true;
        }

        if (item.referencia) {
          const baselineWater = this.parseConsumptionNumber(item.referencia.baselineWater);
          const baselineSewer = this.parseConsumptionNumber(item.referencia.baselineSewer);
          const baselineTotal = this.parseConsumptionNumber(item.referencia.baselineTotal);
          row.baselineWater = baselineWater;
          row.baselineSewer = baselineSewer;
          row.baselineTotal = baselineTotal !== null
            ? baselineTotal
            : (baselineWater !== null || baselineSewer !== null)
              ? Number((Number(baselineWater || 0) + Number(baselineSewer || 0)).toFixed(2))
              : null;
          if (item.metaEconomiaPercentual !== null && item.metaEconomiaPercentual !== undefined) {
            row.metaEconomiaPercentual = item.metaEconomiaPercentual;
          }
          row.hasValues = true;
        }

        const monetaryReference = this.resolveMonetaryReferenceForMonth(monthNumber);
        if (monetaryReference !== null && monetaryReference !== undefined) {
          row.monetaryReference = monetaryReference;
        }

        const driverReference = this.resolveDriverReferenceForMonth(monthNumber, item.driver);
        if (driverReference !== null && driverReference !== undefined && driverReference !== "") {
          row.driverReference = Number(driverReference);
        }

        const driverUnitLabel = this.resolveDriverUnitLabel(item.driver);
        if (driverUnitLabel) {
          row.driverUnitLabel = driverUnitLabel;
        }

        const historicCost = this.parseConsumptionNumber(item.historicCost);
        if (historicCost !== null) {
          row.historicCost = Number((Number(row.historicCost || 0) + historicCost).toFixed(2));
          row.hasValues = true;
        }

        const historicDriverValue = this.parseConsumptionNumber(item.historicDriverValue);
        if (historicDriverValue !== null) {
          row.historicDriverValue = historicDriverValue;
          row.hasValues = true;
        }

        row.pointsCount += 1;
      });

      return Object.values(grouped)
        .filter((row) => row.hasValues)
        .map((row) => {
          const consumptionWater = Number(row.consumptionWater.toFixed(2));
          const consumptionSewer = Number(row.consumptionSewer.toFixed(2));
          const consumptionTotal = Number((row.consumptionWater + row.consumptionSewer).toFixed(2));
          const goalWater = Number(row.goalWater.toFixed(2));
          const goalSewer = Number(row.goalSewer.toFixed(2));
          const goalTotal = Number((row.goalWater + row.goalSewer).toFixed(2));
          const savingWater = row.baselineWater !== null && row.baselineWater !== undefined
            ? this.toPositiveSaving(Number(row.baselineWater || 0) - row.consumptionWater)
            : null;
          const savingSewer = row.baselineSewer !== null && row.baselineSewer !== undefined
            ? this.toPositiveSaving(Number(row.baselineSewer || 0) - row.consumptionSewer)
            : null;
          const baselineConsumptionPerDriver = row.baselineTotal !== null
            && row.driverReference !== null
            && Number(row.driverReference) > 0
            ? Number((Number(row.baselineTotal) / Number(row.driverReference)).toFixed(4))
            : null;
          const historicalConsumptionPerDriver = row.historicDriverValue !== null
            && Number(row.historicDriverValue) > 0
            ? Number((consumptionTotal / Number(row.historicDriverValue)).toFixed(4))
            : null;
          const monetaryReferencePerDriver = row.monetaryReference !== null
            && row.driverReference !== null
            && Number(row.driverReference) > 0
            ? Number((Number(row.monetaryReference) / Number(row.driverReference)).toFixed(4))
            : null;
          const adjustedMonetaryReference = monetaryReferencePerDriver !== null
            && row.historicDriverValue !== null
            && Number(row.historicDriverValue) > 0
            ? Number((monetaryReferencePerDriver * Number(row.historicDriverValue)).toFixed(2))
            : null;
          const effectiveMonetaryReference = adjustedMonetaryReference !== null
            ? adjustedMonetaryReference
            : row.monetaryReference;
          const realEconomy = effectiveMonetaryReference !== null && row.historicCost !== null
            ? this.toPositiveSaving(Number(effectiveMonetaryReference) - row.historicCost)
            : null;

          return {
            ...row,
            consumptionWater,
            consumptionSewer,
            consumptionTotal,
            goalWater,
            goalSewer,
            goalTotal,
            savingWater,
            savingSewer,
            baselineConsumptionPerDriver,
            historicalConsumptionPerDriver,
            monetaryReferencePerDriver,
            adjustedMonetaryReference,
            realEconomy,
          };
        })
        .map((row) => ({
          ...row,
          savingTotal: row.savingWater !== null || row.savingSewer !== null
            ? Number((Number(row.savingWater || 0) + Number(row.savingSewer || 0)).toFixed(2))
            : null,
        }))
        .sort((a, b) => a.monthKey.localeCompare(b.monthKey))
        .map((row, index, rows) => {
          const previousRow = index > 0 ? rows[index - 1] : null;
          const monthComparisons = {};

          BASELINE_TABLE_CALCULATION_KEYS.forEach((columnKey) => {
            monthComparisons[columnKey] = this.buildBaselineTableMonthComparison(row, previousRow, columnKey);
          });

          return {
            ...row,
            monthComparisons,
          };
        });
    },

    monthlyBaselineTableRows() {
      return this.baselineTableRows;
    },

    monthlyBaselineTableGroups() {
      const baselineColumns = [
        { key: "baselineWater", label: "Água" },
        { key: "baselineSewer", label: "Efluentes" },
        { key: "baselineTotal", label: "Total" },
        { key: "goalTotal", label: "Meta total" },
        { key: "monetaryReference", label: "Ref. monetária" },
      ];

      const historicalColumns = [
        { key: "consumptionWater", label: "Água" },
        { key: "consumptionSewer", label: "Efluentes" },
        { key: "consumptionTotal", label: "Total" },
        { key: "historicCost", label: "Conta real" },
      ];

      const calculationColumns = [];

      if (this.hasDriverComparisonColumns) {
        baselineColumns.push({ key: "driverReference", label: "Driver baseline" });
        historicalColumns.push({ key: "historicDriverValue", label: "Driver histórico" });
        calculationColumns.push(
          { key: "baselineConsumptionPerDriver", label: "Baseline / driver", isCalculated: true },
          { key: "historicalConsumptionPerDriver", label: "Histórico / driver", isCalculated: true },
          { key: "monetaryReferencePerDriver", label: "Ref. unitária", isCalculated: true },
          { key: "adjustedMonetaryReference", label: "Ref. ajustada", isCalculated: true },
          { key: "realEconomy", label: "Economia real", isCalculated: true },
          { key: "savingTotal", label: "Economia atual", isCalculated: true },
        );
      } else {
        calculationColumns.push(
          { key: "realEconomy", label: "Economia real", isCalculated: true },
          { key: "savingTotal", label: "Economia atual", isCalculated: true },
        );
      }

      return [
        {
          key: "baseline",
          label: "Dados da baseline",
          description: "Referência cadastrada",
          columns: baselineColumns,
        },
        {
          key: "historical",
          label: "Histórico da unidade",
          description: "Valores reais do mês",
          columns: historicalColumns,
        },
        {
          key: "calculated",
          label: "Cálculos",
          description: "Fórmulas e comparações",
          columns: calculationColumns,
        },
      ];
    },

    monthlyBaselineTableColumns() {
      return this.monthlyBaselineTableGroups.flatMap((group) => group.columns.map((column, index) => ({
        ...column,
        groupKey: group.key,
        isGroupStart: index === 0,
        isGroupEnd: index === group.columns.length - 1,
      })));
    },

    periodHelperText() {
      if (!this.currentBaselineConfig) {
        return "O período será preenchido quando a baseline atual for carregada.";
      }

      return `Pré-set baseado na vigência da baseline atual: ${this.formatDisplayDate(this.currentBaselineConfig.validFrom)}${this.currentBaselineConfig.validTo ? ` - ${this.formatDisplayDate(this.currentBaselineConfig.validTo)}` : " - aberto"}.`;
    },
  },

  watch: {
    getSelectedUnits: {
      immediate: true,
      handler(units) {
        if (Array.isArray(units) && units.length > 1) {
          const lastUnit = units[units.length - 1];
          this.setSelectedUnits([lastUnit]);
          this.setSelectedUnit(lastUnit);
          return;
        }

        this.loadBaselineAndData({ silent: true });
      }
    },

    getSelectedUnit: {
      immediate: true,
      handler() {
        this.loadBaselineAndData({ silent: true });
      }
    },

    localDateFrom() {
      if (!this.isInitializingPeriod) {
        this.loadAllData({ silent: true });
      }
    },

    localDateTo() {
      if (!this.isInitializingPeriod) {
        this.loadAllData({ silent: true });
      }
    },
  },

  methods: {
    ...mapActions(["setSelectedUnit", "setSelectedUnits"]),

    normalizeUnit(unit) {
      if (!unit) return null;
      return {
        ...(unit.unit || {}),
        ...unit,
        id: unit.id || (unit.unit ? unit.unit.id : null),
      };
    },

    formatDateForPayload(value) {
      if (!value) return "";

      if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return this.toLocalDateString(value);
      }

      if (typeof value === "string") {
        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
        const parsed = new Date(value);
        if (!Number.isNaN(parsed.getTime())) return this.toLocalDateString(parsed);
      }

      if (typeof value === "number") {
        const parsed = new Date(String(value).length === 10 ? value * 1000 : value);
        if (!Number.isNaN(parsed.getTime())) return this.toLocalDateString(parsed);
      }

      return "";
    },

    toMonthInputValue(value) {
      if (!value) return "";
      if (typeof value === "string") {
        const match = value.match(/^(\d{4})-(\d{2})/);
        if (match) return `${match[1]}-${match[2]}`;
      }
      const parsed = value instanceof Date ? value : new Date(value);
      if (Number.isNaN(parsed.getTime())) return "";
      const year = parsed.getUTCFullYear();
      const month = String(parsed.getUTCMonth() + 1).padStart(2, "0");
      return `${year}-${month}`;
    },

    monthInputToPayloadDate(value, boundary = "start") {
      const monthValue = this.toMonthInputValue(value);
      if (!monthValue) return "";
      const [year, month] = monthValue.split("-").map(Number);
      const date = boundary === "end"
        ? new Date(Date.UTC(year, month, 0, 12, 0, 0, 0))
        : new Date(Date.UTC(year, month - 1, 1, 12, 0, 0, 0));
      return Number.isNaN(date.getTime()) ? "" : this.toLocalDateString(date);
    },

    toLocalDateString(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },

    formatDisplayDate(value) {
      const payloadDate = this.formatDateForPayload(value);
      if (!payloadDate) return "";
      const parts = payloadDate.split("-");
      if (parts.length !== 3) return "";
      return `${parts[1]}/${parts[0]}`;
    },

    formatNumber(value, digits = 2) {
      if (value === null || value === undefined || value === "") return "-";
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return "-";
      const absoluteValue = Math.abs(numeric);
      const minimumFractionDigits = absoluteValue > 0 && absoluteValue < 1 ? Math.min(4, digits + 2) : digits;
      const maximumFractionDigits = absoluteValue > 0 && absoluteValue < 1 ? 4 : digits;
      return numeric.toLocaleString("pt-BR", {
        minimumFractionDigits,
        maximumFractionDigits,
      });
    },

    formatVolume(value) {
      if (value === null || value === undefined || value === "") return "-";
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return "-";
      return `${this.formatNumber(this.scaleVolumeValue(numeric))} ${this.volumeSuffix}`;
    },

    formatPercent(value) {
      if (value === null || value === undefined || value === "") return "-";
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return "-";
      return `${this.formatNumber(numeric)}%`;
    },

    formatDriverValue(value) {
      if (value === null || value === undefined || value === "") return "-";
      const suffix = this.driverUnitLabel || "";
      return `${this.formatNumber(value)}${suffix ? ` ${suffix}` : ""}`;
    },

    getDriverFactorFormula() {
      if (!this.goalCalculation) return "-";

      const driver = this.goalCalculation.driver || {};
      const factor = this.formatNumber(this.goalCalculation.fatorAplicado);

      if (!driver.enabled) {
        return `Sem driver = ${factor}`;
      }

      const referenceValue = Number(driver.referenceValue);
      const actualValue = Number(driver.actualValue);

      if (Number.isFinite(referenceValue) && referenceValue > 0 && Number.isFinite(actualValue) && actualValue > 0) {
        return `${this.formatDriverValue(actualValue)} / ${this.formatDriverValue(referenceValue)} = ${factor}`;
      }

      return `Fator neutro = ${factor}`;
    },

    getDriverFactorHint() {
      if (!this.goalCalculation) return "";

      const driver = this.goalCalculation.driver || {};

      if (!driver.enabled) {
        return "Driver desabilitado; a baseline não recebe ajuste.";
      }

      const referenceValue = Number(driver.referenceValue);
      const actualValue = Number(driver.actualValue);

      if (Number.isFinite(referenceValue) && referenceValue > 0 && Number.isFinite(actualValue) && actualValue > 0) {
        return "Fórmula: valor atual dividido pelo valor de referência.";
      }

      if (!Number.isFinite(referenceValue) || referenceValue <= 0) {
        return "Valor de referência ausente ou zero; o fator permanece 1.";
      }

      return "Valor atual ausente ou zero; o fator permanece 1.";
    },

    formatBoolean(value) {
      if (value === true) return "Sim";
      if (value === false) return "Não";
      return "-";
    },

    formatCompactNumber(value, digits = 2) {
      if (value === null || value === undefined || value === "") return "-";
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return "-";
      const absoluteValue = Math.abs(numeric);
      const maximumFractionDigits = absoluteValue > 0 && absoluteValue < 1 ? 4 : digits;
      return numeric.toLocaleString("pt-BR", {
        minimumFractionDigits: 0,
        maximumFractionDigits,
      });
    },

    formatMoney(value) {
      if (value === null || value === undefined || value === "") return "-";
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return "-";

      return numeric.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    getRowDriverUnitLabel(row = {}) {
      return row.driverUnitLabel || this.driverUnitLabel || "";
    },

    formatDriverMetric(value, row = {}) {
      if (value === null || value === undefined || value === "") return "-";
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return "-";
      const formattedValue = this.formatCompactNumber(numeric);
      const unitLabel = this.getRowDriverUnitLabel(row);
      return unitLabel ? `${formattedValue} ${unitLabel}` : formattedValue;
    },

    formatVolumePerDriver(value, row = {}) {
      if (value === null || value === undefined || value === "") return "-";
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return "-";
      const unitLabel = this.getRowDriverUnitLabel(row);
      return `${this.formatCompactNumber(this.scaleVolumeValue(numeric))} ${this.volumeSuffix}${unitLabel ? `/${unitLabel}` : ""}`;
    },

    formatMoneyPerDriver(value, row = {}) {
      if (value === null || value === undefined || value === "") return "-";
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return "-";
      const unitLabel = this.getRowDriverUnitLabel(row);
      return `${this.formatMoney(numeric)}${unitLabel ? ` / ${unitLabel}` : ""}`;
    },

    toPositiveSaving(value) {
      const numeric = Number(value);
      if (!Number.isFinite(numeric) || numeric <= 0) return 0;
      return Number(numeric.toFixed(2));
    },

    scaleVolumeValue(value) {
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return null;
      return this.unitMeasureVuex === "liters" ? numeric * 1000 : numeric;
    },

    getDaysInReferenceMonth() {
      if (!this.goalCalculation || !this.goalCalculation.periodDate) return null;
      const date = new Date(this.goalCalculation.periodDate);
      if (Number.isNaN(date.getTime())) return null;
      return new Date(date.getUTCFullYear(), date.getUTCMonth() + 1, 0).getDate();
    },

    getGoalCardTitle(metric) {
      if (this.isFixedBaselineReference) {
        const fixedTitles = {
          water: "Meta de água acumulada no período",
          sewer: "Meta de efluentes acumulada no período",
          total: "Meta total acumulada no período",
        };
        return fixedTitles[metric] || "Meta acumulada no período";
      }

      const defaultTitles = {
        water: "Meta água",
        sewer: "Meta efluentes",
        total: "Meta total",
      };
      return defaultTitles[metric] || "Meta";
    },

    getGoalCardTooltip(metric) {
      const displayedMap = {
        water: this.activeSummary.goalWaterNumeric,
        sewer: this.activeSummary.goalSewerNumeric,
        total: this.activeSummary.goalTotalNumeric,
      };

      if (this.isDistributedFixedBaselineView && this.goalCalculation && this.goalCalculation.referencia) {
        const baselineMap = {
          water: this.goalCalculation.referencia.baselineWater,
          sewer: this.goalCalculation.referencia.baselineSewer,
          total: this.goalCalculation.referencia.baselineTotal,
        };
        const monthlyGoalMap = {
          water: this.goalCalculation.metaAgua,
          sewer: this.goalCalculation.metaEsgoto,
          total: this.goalCalculation.metaTotal,
        };

        const daysInMonth = this.getDaysInReferenceMonth();
        const divisor = this.chartGranularity === "hour" && daysInMonth ? daysInMonth * 24 : daysInMonth;
        const pointGoal = divisor ? Number(monthlyGoalMap[metric] / divisor) : null;

        return `Baseline fixa mensal: ${this.formatCompactNumber(baselineMap[metric])}. Meta mensal após economia: ${this.formatCompactNumber(monthlyGoalMap[metric])}. Meta média por ponto: ${this.formatCompactNumber(pointGoal)}. Valor exibido no card: soma dos pontos do período = ${this.formatCompactNumber(displayedMap[metric])}.`;
      }

      if (this.isFixedBaselineReference && this.goalCalculation && this.goalCalculation.referencia) {
        const monthlyGoalMap = {
          water: this.goalCalculation.metaAgua,
          sewer: this.goalCalculation.metaEsgoto,
          total: this.goalCalculation.metaTotal,
        };

        return `Valor acumulado no período selecionado. Em granularidade mensal, soma as metas mensais do período. Meta mensal de referência: ${this.formatCompactNumber(monthlyGoalMap[metric])}. Total exibido: ${this.formatCompactNumber(displayedMap[metric])}.`;
      }

      if (this.isMonthlyBaselineReference) {
        return `Valor acumulado no período selecionado. Cada ponto usa a baseline mensal correspondente e aplica a meta de economia antes da soma final do card. Total exibido: ${this.formatCompactNumber(displayedMap[metric])}.`;
      }

      return `Valor acumulado no período selecionado, somando as metas calculadas para cada ponto da série. Total exibido: ${this.formatCompactNumber(displayedMap[metric])}.`;
    },

    getSavingCardTooltip(metric) {
      const baselineMap = {
        water: this.activeSummary.savingBaselineWaterNumeric ?? this.activeSummary.baselineWaterNumeric,
        sewer: this.activeSummary.savingBaselineSewerNumeric ?? this.activeSummary.baselineSewerNumeric,
      };
      const consumptionMap = {
        water: this.activeSummary.savingConsumptionWaterNumeric ?? this.activeSummary.consumptionWaterNumeric,
        sewer: this.activeSummary.savingConsumptionSewerNumeric ?? this.activeSummary.consumptionSewerNumeric,
      };
      const savingMap = {
        water: this.activeSummary.savingWaterNumeric,
        sewer: this.activeSummary.savingSewerNumeric,
        total: this.activeSummary.savingTotalNumeric,
      };
      const label = metric === "water" ? "água" : "efluentes";

      if (metric === "total") {
        return `Economia total no período: economia de água + economia de efluentes. Cálculo: ${this.formatVolume(savingMap.water)} + ${this.formatVolume(savingMap.sewer)} = ${this.formatVolume(savingMap.total)}. Economias negativas são exibidas como 0.`;
      }

      return `Economia de ${label} no período: baseline acumulada - consumo real. Cálculo: ${this.formatVolume(baselineMap[metric])} - ${this.formatVolume(consumptionMap[metric])} = ${this.formatVolume(savingMap[metric])}. Quando o consumo fica acima da baseline, a economia exibida é 0.`;
    },

    getDriverComparisonAverageTooltip() {
      const kpi = this.driverComparisonAverageKpi;
      if (!kpi) return "Sem meses reais suficientes para calcular a média do driver.";

      const averageLabel = this.formatVolumePerDriver(kpi.historicalAverage, kpi);
      const baselineLabel = this.formatVolumePerDriver(kpi.baselineAverage, kpi);
      const deltaLabel = this.formatVolumePerDriver(kpi.averageDelta, kpi);

      if (kpi.baselineAverage !== null) {
        return `Média dos comparativos reais por driver no período. Valor principal: consumo real total dividido pelo driver histórico de cada mês e depois consolidado pela média do período = ${averageLabel}. Baseline média por driver no mesmo recorte = ${baselineLabel}. Diferença média = ${deltaLabel}. Meses considerados: ${kpi.monthsCount}.`;
      }

      return `Média dos comparativos reais por driver no período = ${averageLabel}. Meses considerados: ${kpi.monthsCount}.`;
    },

    getFinancialDriverComparisonAverageTooltip() {
      const kpi = this.financialDriverComparisonAverageKpi;
      if (!kpi) return "Sem meses reais suficientes para calcular a média financeira por driver.";

      const actualLabel = this.formatMoneyPerDriver(kpi.actualAverage, kpi);
      const baselineLabel = this.formatMoneyPerDriver(kpi.baselineAverage, kpi);
      const deltaLabel = this.formatMoneyPerDriver(kpi.averageDelta, kpi);

      if (kpi.baselineAverage !== null) {
        return `Média financeira real por driver no período. Valor principal: custo real da conta dividido pelo driver histórico de cada mês e consolidado pela média do período = ${actualLabel}. Referência monetária média por driver da baseline = ${baselineLabel}. Diferença média = ${deltaLabel}. Meses considerados: ${kpi.monthsCount}.`;
      }

      return `Média financeira real por driver no período = ${actualLabel}. Meses considerados: ${kpi.monthsCount}.`;
    },

    getTotalFinancialEconomyTooltip() {
      const kpi = this.totalFinancialEconomyKpi;
      if (!kpi) return "Sem dados financeiros suficientes para calcular a economia total do período.";

      const totalLabel = this.formatMoney(kpi.total);
      return `Economia financeira total no período: soma da economia real de cada mês com referência monetária e conta real disponíveis. Total = ${totalLabel}. Meses considerados: ${kpi.monthsCount}. Quando o custo real supera a referência, o mês contribui com R$ 0.`;
    },

    getMonetaryReferenceFromObject(source) {
      if (!source || typeof source !== "object") return null;

      const candidates = [
        source.reference_economy,
      ];

      const value = candidates.find((candidate) => candidate !== null && candidate !== undefined && candidate !== "");
      if (value === null || value === undefined) return null;

      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : null;
    },

    resolveMonetaryReferenceForMonth(month) {
      const config = this.currentBaselineConfig || {};
      const monthlyBaseline = config.monthlyBaseline || {};
      const monthlyEntry = monthlyBaseline[String(month)] || monthlyBaseline[month] || null;

      const monthlyReference = this.getMonetaryReferenceFromObject(monthlyEntry);
      if (monthlyReference !== null && monthlyReference !== undefined) return monthlyReference;

      const fixedReference = this.getMonetaryReferenceFromObject(config.fixedBaseline);
      if (fixedReference !== null && fixedReference !== undefined) return fixedReference;

      return this.getMonetaryReferenceFromObject(config);
    },

    resolveDriverReferenceForMonth(month, pointDriver = null) {
      const config = this.currentBaselineConfig || {};
      const driver = config.driver || pointDriver || {};

      if (!driver || !driver.isEnabled) return null;

      const monthlyBaseline = config.monthlyBaseline || {};
      const monthlyEntry = monthlyBaseline[String(month)] || monthlyBaseline[month] || null;

      if (monthlyEntry && monthlyEntry.driverReferenceValue !== null && monthlyEntry.driverReferenceValue !== undefined && monthlyEntry.driverReferenceValue !== "") {
        return monthlyEntry.driverReferenceValue;
      }

      if (driver.driverReferenceValue !== null && driver.driverReferenceValue !== undefined && driver.driverReferenceValue !== "") {
        return driver.driverReferenceValue;
      }

      return pointDriver && pointDriver.referenceValue !== null && pointDriver.referenceValue !== undefined
        ? pointDriver.referenceValue
        : null;
    },

    resolveDriverUnitLabel(pointDriver = null) {
      const configDriver = this.currentBaselineConfig && this.currentBaselineConfig.driver
        ? this.currentBaselineConfig.driver
        : {};
      const driver = pointDriver || {};

      return configDriver.driverUnitLabel || driver.unitLabel || "";
    },

    getReferenceFormula(metric) {
      if (!this.goalCalculation || !this.goalCalculation.referencia) return "";

      const reference = this.goalCalculation.referencia;
      const economy = this.goalCalculation.metaEconomiaPercentual;

      if (metric === "water") {
        return `Baseline água: ${this.formatVolume(reference.baselineWater)}. Meta de economia: ${this.formatCompactNumber(economy)}%. Cálculo: ${this.formatVolume(reference.baselineWater)} - ${this.formatCompactNumber(economy)}% = ${this.formatVolume(this.goalCalculation.metaAgua)}.`;
      }

      if (metric === "sewer") {
        return `Baseline efluentes: ${this.formatVolume(reference.baselineSewer)}. Meta de economia: ${this.formatCompactNumber(economy)}%. Cálculo: ${this.formatVolume(reference.baselineSewer)} - ${this.formatCompactNumber(economy)}% = ${this.formatVolume(this.goalCalculation.metaEsgoto)}.`;
      }

      if (metric === "total") {
        return `Meta total mensal: soma da meta de água mensal com a meta de efluentes mensal. Cálculo: ${this.formatVolume(this.goalCalculation.metaAgua)} + ${this.formatVolume(this.goalCalculation.metaEsgoto)} = ${this.formatVolume(this.goalCalculation.metaTotal)}.`;
      }

      if (metric === "economy") {
        return "Percentual de redução aplicado sobre a baseline para chegar à meta mensal.";
      }

      return "";
    },

    validateBeforeRequest() {
      if (this.hasMultipleUnitsSelected) {
        return "Selecione apenas uma unidade para analisar metas.";
      }

      if (!this.selectedUnit || !this.selectedUnit.id) {
        return "Selecione uma unidade para consultar metas.";
      }

      if (!this.dateRangePayload.dateFrom || !this.dateRangePayload.dateTo) {
        return "Selecione um período válido para consultar metas.";
      }

      if (new Date(this.dateRangePayload.dateFrom) > new Date(this.dateRangePayload.dateTo)) {
        return "A data inicial não pode ser maior que a data final.";
      }

      return "";
    },

    resolveChartGranularity(period) {
      if (period === "month") return "month";
      if (period === "hour") return "hour";
      return "day";
    },

    getMonthlyTableTooltip(column) {
      const tooltips = {
        month: "Mês consolidado dentro do período selecionado.",
        baselineWater: "Valor de água cadastrado na baseline para o mês.",
        baselineSewer: "Valor de efluentes cadastrado na baseline para o mês.",
        baselineTotal: "Soma de água e efluentes da baseline do mês.",
        monetaryReference: "Referência monetária cadastrada na baseline para o mês.",
        driverReference: "Valor de driver cadastrado na baseline para o mês.",
        consumptionWater: "Consumo real de água vindo do histórico da unidade ou da leitura consolidada do mês atual.",
        consumptionSewer: "Consumo real de efluentes vindo do histórico da unidade ou da leitura consolidada do mês atual.",
        consumptionTotal: "Soma do consumo real de água e efluentes do mês.",
        historicCost: "Valor real da conta registrado no histórico da unidade para o mês.",
        historicDriverValue: "Valor do driver informado no histórico da unidade para o mês.",
        goalTotal: "Meta total do mês a partir da baseline e do percentual de economia. Ainda sem ajuste pelo driver histórico.",
        savingTotal: "Economia volumétrica atual da tela: baseline total menos consumo total, limitada a zero quando o consumo supera a baseline.",
        baselineConsumptionPerDriver: "Consumo total da baseline dividido pelo driver da baseline. Mostra o consumo normalizado por driver.",
        historicalConsumptionPerDriver: "Consumo total real do mês dividido pelo driver informado no histórico da unidade.",
        monetaryReferencePerDriver: "Referência monetária da baseline dividida pelo driver da baseline. Exemplo: R$ por hóspede.",
        adjustedMonetaryReference: "Referência monetária unitária da baseline multiplicada pelo driver do histórico. Representa a referência monetária ajustada ao cenário real do mês.",
        realEconomy: "Economia real do mês. Com driver, usa a referência monetária ajustada pelo driver histórico. Sem driver, usa a referência monetária da baseline. Em ambos os casos, subtrai o valor real da conta do histórico e, se o resultado for negativo, exibe 0.",
      };

      return tooltips[column] || "";
    },

    formatBaselineTableValue(row, columnKey) {
      const volumeColumns = [
        "baselineWater",
        "baselineSewer",
        "baselineTotal",
        "consumptionWater",
        "consumptionSewer",
        "consumptionTotal",
        "goalTotal",
        "savingTotal",
      ];
      const moneyColumns = ["monetaryReference", "historicCost", "adjustedMonetaryReference", "realEconomy"];

      if (volumeColumns.includes(columnKey)) {
        return this.formatVolume(row[columnKey]);
      }

      if (moneyColumns.includes(columnKey)) {
        return this.formatMoney(row[columnKey]);
      }

      if (columnKey === "driverReference" || columnKey === "historicDriverValue") {
        return this.formatDriverMetric(row[columnKey], row);
      }

      if (columnKey === "baselineConsumptionPerDriver" || columnKey === "historicalConsumptionPerDriver") {
        return this.formatVolumePerDriver(row[columnKey], row);
      }

      if (columnKey === "monetaryReferencePerDriver") {
        return this.formatMoneyPerDriver(row[columnKey], row);
      }

      return row[columnKey] !== null && row[columnKey] !== undefined ? row[columnKey] : "-";
    },

    getBaselineTableNumericValue(row, columnKey) {
      if (!row) return null;
      const value = row[columnKey];
      if (value === null || value === undefined || value === "") return null;
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : null;
    },

    buildBaselineTableMonthComparison(currentRow, previousRow, columnKey) {
      const currentValue = this.getBaselineTableNumericValue(currentRow, columnKey);
      const previousValue = this.getBaselineTableNumericValue(previousRow, columnKey);

      if (!previousRow || currentValue === null || previousValue === null) {
        return null;
      }

      if (currentValue === previousValue) {
        return {
          direction: "equal",
          icon: "mdi-minus",
          color: "#7a8da3",
          tooltip: `Sem alteração em relação a ${previousRow.label} (${this.formatBaselineTableValue(previousRow, columnKey)}).`,
        };
      }

      const increased = currentValue > previousValue;

      return {
        direction: increased ? "up" : "down",
        icon: increased ? "mdi-arrow-up" : "mdi-arrow-down",
        color: this.getBaselineTableComparisonColor(columnKey, increased),
        tooltip: `${increased ? "Aumentou" : "Diminuiu"} em relação a ${previousRow.label}: ${this.formatBaselineTableValue(previousRow, columnKey)} → ${this.formatBaselineTableValue(currentRow, columnKey)}.`,
      };
    },

    getBaselineTableComparisonColor(columnKey, increased) {
      const higherIsBetter = ["savingTotal", "realEconomy"];
      const lowerIsBetter = [
        "baselineConsumptionPerDriver",
        "historicalConsumptionPerDriver",
        "adjustedMonetaryReference",
      ];

      if (higherIsBetter.includes(columnKey)) {
        return increased ? "#2e7d5a" : "#c45c4a";
      }

      if (lowerIsBetter.includes(columnKey)) {
        return increased ? "#c45c4a" : "#2e7d5a";
      }

      return increased ? "#3d6ea8" : "#6d8096";
    },

    parseDriverActualValue() {
      if (!this.hasDriverActualValue) return undefined;
      const normalized = String(this.driverActualValue).replace(",", ".");
      const numeric = Number(normalized);
      return Number.isFinite(numeric) ? numeric : undefined;
    },

    buildTimelineKey(value, granularity) {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "";

      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, "0");
      const day = String(date.getUTCDate()).padStart(2, "0");
      const hour = String(date.getUTCHours()).padStart(2, "0");

      if (granularity === "month") return `${year}-${month}`;
      if (granularity === "hour") return `${year}-${month}-${day}T${hour}`;
      return `${year}-${month}-${day}`;
    },

    formatTimelineLabel(value, granularity) {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "-";

      const day = String(date.getUTCDate()).padStart(2, "0");
      const month = String(date.getUTCMonth() + 1).padStart(2, "0");
      const year = date.getUTCFullYear();
      const hour = String(date.getUTCHours()).padStart(2, "0");

      if (granularity === "month") return `${month}/${year}`;
      if (granularity === "hour") return `${day}/${month} ${hour}h`;
      return `${day}/${month}`;
    },

    getCurrentMonthKey() {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      return `${year}-${month}`;
    },

    getMonthKeyFromDate(value) {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "";

      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, "0");
      return `${year}-${month}`;
    },

    getMonthStartPayload(month) {
      const range = this.getMonthRange(month);
      return range ? this.toLocalDateString(range.start) : "";
    },

    getMonthEndPayload(month) {
      const range = this.getMonthRange(month);
      if (!range) return "";

      const currentMonth = this.getCurrentMonthKey();
      if (month === currentMonth) {
        return this.toLocalDateString(new Date());
      }

      return this.toLocalDateString(range.end);
    },

    getMonthRange(month) {
      const match = String(month || "").match(/^(\d{4})-(\d{2})$/);
      if (!match) return null;

      const year = Number(match[1]);
      const monthIndex = Number(match[2]) - 1;

      if (!Number.isFinite(year) || !Number.isFinite(monthIndex) || monthIndex < 0 || monthIndex > 11) {
        return null;
      }

      return {
        start: new Date(Date.UTC(year, monthIndex, 1, 12, 0, 0)),
        end: new Date(Date.UTC(year, monthIndex + 1, 0, 12, 0, 0)),
      };
    },

    getDateRangeForFilter(dateFrom, dateTo) {
      const from = dateFrom ? new Date(dateFrom) : null;
      const to = dateTo ? new Date(dateTo) : null;

      return {
        from: from && !Number.isNaN(from.getTime()) ? from : null,
        to: to && !Number.isNaN(to.getTime()) ? to : null,
      };
    },

    isMonthInsideRanges(month, ranges) {
      const monthRange = this.getMonthRange(month);
      if (!monthRange) return false;

      return ranges.every((range) => {
        if (!range || (!range.from && !range.to)) return true;
        if (range.from && monthRange.end < range.from) return false;
        if (range.to && monthRange.start > range.to) return false;
        return true;
      });
    },

    getHistoricMonth(point) {
      return point && (point.month || point.competence) ? point.month || point.competence : "";
    },

    normalizeHistoricPoint(point) {
      const month = this.getHistoricMonth(point);
      if (!point || point.deletedAt || !month) return null;

      return {
        date: `${month}-01T12:00:00.000Z`,
        consumptionWater: Number(point.water_cubic_meters || 0),
        consumptionSewer: Number(point.sewer_cubic_meters || 0),
        consumptionTotal: Number(
          Number(point.water_cubic_meters || 0)
          + Number(point.sewer_cubic_meters || 0)
        ),
        historicCost: this.parseConsumptionNumber(point.cost_consumption),
        historicDriverValue: this.parseConsumptionNumber(point.driver_reference_value),
        isEstimative: false,
      };
    },

    normalizePlatformConsumptionPoint(point) {
      if (!point || !point.time) return null;

      return {
        date: point.time,
        consumptionWater: Number(point.valueWater || 0),
        consumptionSewer: Number(point.valueSewer || 0),
        consumptionTotal: Number(
          point.valueTotal || (Number(point.valueWater || 0) + Number(point.valueSewer || 0))
        ),
        isEstimative: Boolean(point.isEstimative),
      };
    },

    normalizeHistoricSeries(historics) {
      const baselineRange = this.currentBaselineConfig
        ? this.getDateRangeForFilter(this.currentBaselineConfig.validFrom, this.currentBaselineConfig.validTo)
        : null;
      const selectedRange = this.getDateRangeForFilter(this.dateRangePayload.dateFrom, this.dateRangePayload.dateTo);
      const ranges = [baselineRange, selectedRange].filter(Boolean);

      return (Array.isArray(historics) ? historics : [])
        .filter((point) => this.isMonthInsideRanges(this.getHistoricMonth(point), ranges))
        .map((point) => this.normalizeHistoricPoint(point))
        .filter(Boolean);
    },

    normalizePlatformConsumptionSeries(response) {
      const rawPoints = response && response.data && response.data.data && Array.isArray(response.data.data.consumptions)
        ? response.data.data.consumptions
        : [];

      return rawPoints
        .map((point) => this.normalizePlatformConsumptionPoint(point))
        .filter(Boolean);
    },

    mergeConsumptionSources(historicPoints, platformPoints) {
      const map = {};

      (Array.isArray(historicPoints) ? historicPoints : []).forEach((point) => {
        const key = this.buildTimelineKey(point.date, this.chartGranularity);
        if (key) map[key] = { ...(map[key] || {}), ...point };
      });

      (Array.isArray(platformPoints) ? platformPoints : []).forEach((point) => {
        const key = this.buildTimelineKey(point.date, this.chartGranularity);
        if (key) map[key] = { ...(map[key] || {}), ...point };
      });

      return Object.values(map).sort((a, b) => new Date(a.date) - new Date(b.date));
    },

    shouldLoadCurrentMonthPlatformConsumption() {
      const currentMonth = this.getCurrentMonthKey();
      const ranges = [
        this.currentBaselineConfig
          ? this.getDateRangeForFilter(this.currentBaselineConfig.validFrom, this.currentBaselineConfig.validTo)
          : null,
        this.getDateRangeForFilter(this.dateRangePayload.dateFrom, this.dateRangePayload.dateTo),
      ].filter(Boolean);

      return this.isMonthInsideRanges(currentMonth, ranges);
    },

    parseConsumptionNumber(value) {
      if (value === null || value === undefined || value === "") return null;

      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : null;
    },

    hasPositiveConsumptionValue(point) {
      if (!point) return false;

      return Number(this.parseConsumptionNumber(point.consumptionWater) || 0) > 0
        || Number(this.parseConsumptionNumber(point.consumptionSewer) || 0) > 0
        || Number(this.parseConsumptionNumber(point.consumptionTotal) || 0) > 0;
    },

    getLastConsumptionMonthKey(consumptionPoints) {
      return (Array.isArray(consumptionPoints) ? consumptionPoints : [])
        .filter((point) => point && !point.isEstimative && this.hasPositiveConsumptionValue(point))
        .map((point) => this.getMonthKeyFromDate(point.date))
        .filter(Boolean)
        .sort()
        .pop() || "";
    },

    getFutureEstimateAverages(consumptionPoints) {
      const totals = {
        water: 0,
        sewer: 0,
        waterCount: 0,
        sewerCount: 0,
      };

      (Array.isArray(consumptionPoints) ? consumptionPoints : []).forEach((point) => {
        if (!point || point.isEstimative) return;
        if (!this.hasPositiveConsumptionValue(point)) return;

        const water = this.parseConsumptionNumber(point.consumptionWater);
        if (water !== null) {
          totals.water += water;
          totals.waterCount += 1;
        }

        const sewer = this.parseConsumptionNumber(point.consumptionSewer);
        if (sewer !== null) {
          totals.sewer += sewer;
          totals.sewerCount += 1;
        }
      });

      if (!totals.waterCount && !totals.sewerCount) return null;

      const waterAverage = totals.waterCount
        ? Number((totals.water / totals.waterCount).toFixed(2))
        : null;
      const sewerAverage = totals.sewerCount
        ? Number((totals.sewer / totals.sewerCount).toFixed(2))
        : null;

      return {
        water: waterAverage,
        sewer: sewerAverage,
        total: Number((Number(waterAverage || 0) + Number(sewerAverage || 0)).toFixed(2)),
      };
    },

    getFutureEstimateRange(consumptionPoints) {
      if (!this.currentBaselineConfig) return null;

      const baselineEnd = this.currentBaselineConfig.validTo
        ? new Date(this.currentBaselineConfig.validTo)
        : null;
      const normalizedBaselineEnd = baselineEnd && !Number.isNaN(baselineEnd.getTime())
        ? baselineEnd
        : null;

      const lastConsumptionMonth = this.getLastConsumptionMonthKey(consumptionPoints);
      const lastConsumptionRange = this.getMonthRange(lastConsumptionMonth);
      if (!lastConsumptionRange) return null;

      const selectedRange = this.getDateRangeForFilter(this.dateRangePayload.dateFrom, this.dateRangePayload.dateTo);
      const endDate = selectedRange.to && normalizedBaselineEnd && selectedRange.to < normalizedBaselineEnd
        ? selectedRange.to
        : normalizedBaselineEnd || selectedRange.to;
      if (!endDate) return null;

      const selectedFrom = selectedRange.from || null;
      const startDate = new Date(lastConsumptionRange.start);
      startDate.setUTCMonth(startDate.getUTCMonth() + 1);
      const endMonthStart = new Date(Date.UTC(endDate.getUTCFullYear(), endDate.getUTCMonth(), 1, 12, 0, 0));

      if (startDate > endMonthStart) return null;

      return {
        startDate,
        endMonthStart,
        selectedFrom,
      };
    },

    getBaselineReferenceMode() {
      const config = this.currentBaselineConfig || {};
      return config.referenceMode || config.mode || null;
    },

    getGoalEconomyPercent() {
      const calculationValue = this.goalCalculation && this.goalCalculation.metaEconomiaPercentual;
      if (calculationValue !== null && calculationValue !== undefined && calculationValue !== "") {
        const numeric = Number(calculationValue);
        if (Number.isFinite(numeric)) return numeric;
      }

      const configValue = this.currentBaselineConfig && this.currentBaselineConfig.metaEconomia;
      const numeric = Number(configValue);
      return Number.isFinite(numeric) ? numeric : 0;
    },

    buildMonthlyEstimatedGoalFields(date) {
      const config = this.currentBaselineConfig || {};
      if (this.getBaselineReferenceMode() !== "monthlyBaseline") return {};

      const monthNumber = String(date.getUTCMonth() + 1);
      const monthlyBaseline = config.monthlyBaseline || {};
      const monthlyEntry = monthlyBaseline[monthNumber] || monthlyBaseline[monthNumber.padStart(2, "0")] || null;
      if (!monthlyEntry) return {};

      const baselineWater = this.parseConsumptionNumber(monthlyEntry.water);
      const baselineSewer = this.parseConsumptionNumber(monthlyEntry.sewer);
      if (baselineWater === null && baselineSewer === null) return {};

      const economyMultiplier = 1 - (this.getGoalEconomyPercent() / 100);
      const metaAgua = baselineWater !== null ? Number((baselineWater * economyMultiplier).toFixed(2)) : null;
      const metaEsgoto = baselineSewer !== null ? Number((baselineSewer * economyMultiplier).toFixed(2)) : null;

      return {
        metaAgua,
        metaEsgoto,
        metaTotal: Number((Number(metaAgua || 0) + Number(metaEsgoto || 0)).toFixed(2)),
        metaEconomiaPercentual: this.getGoalEconomyPercent(),
        referencia: {
          configId: config.id || config.configId || "current-baseline",
          mode: "monthlyBaseline",
          validFrom: config.validFrom || null,
          validTo: config.validTo || null,
          baselineWater,
          baselineSewer,
          baselineTotal: Number((Number(baselineWater || 0) + Number(baselineSewer || 0)).toFixed(2)),
        },
      };
    },

    addFutureEstimatedConsumptionToTimeline(map, consumptionPoints, granularity) {
      if (granularity !== "month" || !map || typeof map !== "object") return;

      const averages = this.getFutureEstimateAverages(consumptionPoints);
      const range = this.getFutureEstimateRange(consumptionPoints);
      if (!averages || !range) return;

      const cursor = new Date(range.startDate);
      while (cursor <= range.endMonthStart) {
        const monthRange = this.getMonthRange(this.getMonthKeyFromDate(cursor));
        if (monthRange && (!range.selectedFrom || monthRange.end >= range.selectedFrom)) {
          const key = this.buildTimelineKey(cursor, granularity);

          if (key) {
            const estimatedGoalFields = this.buildMonthlyEstimatedGoalFields(cursor);

            if (!map[key]) {
              map[key] = {
                key,
                date: cursor.toISOString(),
                metaAgua: estimatedGoalFields.metaAgua !== undefined ? estimatedGoalFields.metaAgua : null,
                metaEsgoto: estimatedGoalFields.metaEsgoto !== undefined ? estimatedGoalFields.metaEsgoto : null,
                metaTotal: estimatedGoalFields.metaTotal !== undefined ? estimatedGoalFields.metaTotal : null,
                metaEconomiaPercentual: estimatedGoalFields.metaEconomiaPercentual !== undefined ? estimatedGoalFields.metaEconomiaPercentual : null,
                referencia: estimatedGoalFields.referencia || null,
                fatorAplicado: null,
                driver: null,
                consumptionWater: null,
                consumptionSewer: null,
                consumptionTotal: null,
                historicCost: null,
                historicDriverValue: null,
                isEstimative: false,
              };
            }

            if (estimatedGoalFields.metaAgua !== undefined && (map[key].metaAgua === null || map[key].metaAgua === undefined)) {
              map[key].metaAgua = estimatedGoalFields.metaAgua;
            }

            if (estimatedGoalFields.metaEsgoto !== undefined && (map[key].metaEsgoto === null || map[key].metaEsgoto === undefined)) {
              map[key].metaEsgoto = estimatedGoalFields.metaEsgoto;
            }

            if (estimatedGoalFields.metaTotal !== undefined && (map[key].metaTotal === null || map[key].metaTotal === undefined)) {
              map[key].metaTotal = estimatedGoalFields.metaTotal;
            }

            if (estimatedGoalFields.metaEconomiaPercentual !== undefined && (map[key].metaEconomiaPercentual === null || map[key].metaEconomiaPercentual === undefined)) {
              map[key].metaEconomiaPercentual = estimatedGoalFields.metaEconomiaPercentual;
            }

            if (estimatedGoalFields.referencia && !map[key].referencia) {
              map[key].referencia = estimatedGoalFields.referencia;
            }

            map[key].consumptionWater = averages.water;
            map[key].consumptionSewer = averages.sewer;
            map[key].consumptionTotal = averages.total;
            map[key].historicCost = null;
            map[key].historicDriverValue = null;
            map[key].isEstimative = true;
          }
        }

        cursor.setUTCMonth(cursor.getUTCMonth() + 1);
      }
    },

    mergeTimeline(goalPoints, consumptionPoints) {
      const map = {};
      const granularity = this.chartGranularity;

      (Array.isArray(goalPoints) ? goalPoints : []).forEach((point) => {
        const key = this.buildTimelineKey(point.date || point.referenceDate, granularity);
        if (!key) return;
        map[key] = {
          key,
          date: point.date || point.referenceDate,
          metaAgua: point.metaAgua,
          metaEsgoto: point.metaEsgoto,
          metaTotal: point.metaTotal,
          metaEconomiaPercentual: point.metaEconomiaPercentual,
          referencia: point.referencia || null,
          fatorAplicado: point.fatorAplicado,
          driver: point.driver || null,
          consumptionWater: null,
          consumptionSewer: null,
          consumptionTotal: null,
          historicCost: null,
          historicDriverValue: null,
          isEstimative: false,
        };
      });

      (Array.isArray(consumptionPoints) ? consumptionPoints : []).forEach((point) => {
        const key = this.buildTimelineKey(point.date, granularity);
        if (!key) return;

        if (!map[key]) {
          map[key] = {
            key,
            date: point.date,
            metaAgua: null,
            metaEsgoto: null,
            metaTotal: null,
            metaEconomiaPercentual: null,
            referencia: null,
            fatorAplicado: null,
            driver: null,
            consumptionWater: null,
            consumptionSewer: null,
            consumptionTotal: null,
            historicCost: null,
            historicDriverValue: null,
            isEstimative: false,
          };
        }

        map[key].consumptionWater = point.consumptionWater;
        map[key].consumptionSewer = point.consumptionSewer;
        map[key].consumptionTotal = point.consumptionTotal;
        if (point.historicCost !== undefined) {
          map[key].historicCost = point.historicCost;
        }
        if (point.historicDriverValue !== undefined) {
          map[key].historicDriverValue = point.historicDriverValue;
        }
        map[key].isEstimative = Boolean(point.isEstimative);
      });

      this.addFutureEstimatedConsumptionToTimeline(map, consumptionPoints, granularity);

      return Object.values(map).sort((a, b) => new Date(a.date) - new Date(b.date));
    },

    summarizeTimeline(timeline, fallbackReference) {
      const list = Array.isArray(timeline) ? timeline : [];
      const summary = {
        consumptionWaterNumeric: null,
        consumptionSewerNumeric: null,
        consumptionTotalNumeric: null,
        goalWaterNumeric: null,
        goalSewerNumeric: null,
        goalTotalNumeric: null,
        baselineWaterNumeric: null,
        baselineSewerNumeric: null,
        baselineTotalNumeric: null,
        savingBaselineWaterNumeric: null,
        savingBaselineSewerNumeric: null,
        savingConsumptionWaterNumeric: null,
        savingConsumptionSewerNumeric: null,
        savingWaterNumeric: null,
        savingSewerNumeric: null,
        savingTotalNumeric: null,
        metaEconomiaPercentual: null,
        pointCount: list.length,
        baselineCount: 0,
        reference: fallbackReference || null,
      };

      let totalConsumptionWater = 0;
      let totalConsumptionSewer = 0;
      let totalGoalWater = 0;
      let totalGoalSewer = 0;
      let totalBaselineWater = 0;
      let totalBaselineSewer = 0;
      let savingConsumptionWater = 0;
      let savingConsumptionSewer = 0;
      let savingBaselineWater = 0;
      let savingBaselineSewer = 0;
      let savingWaterTotal = 0;
      let savingSewerTotal = 0;
      let hasConsumption = false;
      let hasGoal = false;
      let hasBaselineWater = false;
      let hasBaselineSewer = false;
      let hasSavingConsumption = false;
      let hasSavingBaselineWater = false;
      let hasSavingBaselineSewer = false;
      const baselineIds = {};

      list.forEach((item) => {
        const isEstimatedPoint = item && item.isEstimative === true;
        const hasRealConsumptionPoint = !isEstimatedPoint && (
          item.consumptionWater !== null && item.consumptionWater !== undefined
          || item.consumptionSewer !== null && item.consumptionSewer !== undefined
        );

        if (item.consumptionWater !== null && item.consumptionWater !== undefined) {
          totalConsumptionWater += Number(item.consumptionWater || 0);
          hasConsumption = true;
        }

        if (item.consumptionSewer !== null && item.consumptionSewer !== undefined) {
          totalConsumptionSewer += Number(item.consumptionSewer || 0);
          hasConsumption = true;
        }

        if (item.metaAgua !== null && item.metaAgua !== undefined) {
          totalGoalWater += Number(item.metaAgua || 0);
          hasGoal = true;
        }

        if (item.metaEsgoto !== null && item.metaEsgoto !== undefined) {
          totalGoalSewer += Number(item.metaEsgoto || 0);
          hasGoal = true;
        }

        if (item.referencia && item.referencia.configId) {
          baselineIds[item.referencia.configId] = true;
          if (!summary.reference) summary.reference = item.referencia;
        }

        if (item.referencia) {
          if (item.referencia.baselineWater !== null && item.referencia.baselineWater !== undefined) {
            totalBaselineWater += Number(item.referencia.baselineWater || 0);
            hasBaselineWater = true;
          }

          if (item.referencia.baselineSewer !== null && item.referencia.baselineSewer !== undefined) {
            totalBaselineSewer += Number(item.referencia.baselineSewer || 0);
            hasBaselineSewer = true;
          }
        }

        if (hasRealConsumptionPoint) {
          savingConsumptionWater += Number(item.consumptionWater || 0);
          savingConsumptionSewer += Number(item.consumptionSewer || 0);
          hasSavingConsumption = true;

          if (item.referencia) {
            if (item.referencia.baselineWater !== null && item.referencia.baselineWater !== undefined) {
              savingBaselineWater += Number(item.referencia.baselineWater || 0);
              savingWaterTotal += this.toPositiveSaving(Number(item.referencia.baselineWater || 0) - Number(item.consumptionWater || 0));
              hasSavingBaselineWater = true;
            }

            if (item.referencia.baselineSewer !== null && item.referencia.baselineSewer !== undefined) {
              savingBaselineSewer += Number(item.referencia.baselineSewer || 0);
              savingSewerTotal += this.toPositiveSaving(Number(item.referencia.baselineSewer || 0) - Number(item.consumptionSewer || 0));
              hasSavingBaselineSewer = true;
            }
          }
        }
      });

      summary.baselineCount = Object.keys(baselineIds).length;

      if (hasConsumption) {
        summary.consumptionWaterNumeric = Number(totalConsumptionWater.toFixed(2));
        summary.consumptionSewerNumeric = Number(totalConsumptionSewer.toFixed(2));
        summary.consumptionTotalNumeric = Number((totalConsumptionWater + totalConsumptionSewer).toFixed(2));
      }

      if (hasGoal) {
        summary.goalWaterNumeric = Number(totalGoalWater.toFixed(2));
        summary.goalSewerNumeric = Number(totalGoalSewer.toFixed(2));
        summary.goalTotalNumeric = Number((totalGoalWater + totalGoalSewer).toFixed(2));
      }

      if (hasBaselineWater || hasBaselineSewer) {
        summary.baselineWaterNumeric = hasBaselineWater ? Number(totalBaselineWater.toFixed(2)) : null;
        summary.baselineSewerNumeric = hasBaselineSewer ? Number(totalBaselineSewer.toFixed(2)) : null;
        summary.baselineTotalNumeric = Number((totalBaselineWater + totalBaselineSewer).toFixed(2));
      }

      if (hasSavingBaselineWater || hasSavingBaselineSewer) {
        summary.savingBaselineWaterNumeric = hasSavingBaselineWater ? Number(savingBaselineWater.toFixed(2)) : null;
        summary.savingBaselineSewerNumeric = hasSavingBaselineSewer ? Number(savingBaselineSewer.toFixed(2)) : null;

        if (hasSavingConsumption) {
          summary.savingConsumptionWaterNumeric = Number(savingConsumptionWater.toFixed(2));
          summary.savingConsumptionSewerNumeric = Number(savingConsumptionSewer.toFixed(2));
          summary.savingWaterNumeric = hasSavingBaselineWater ? Number(savingWaterTotal.toFixed(2)) : null;
          summary.savingSewerNumeric = hasSavingBaselineSewer ? Number(savingSewerTotal.toFixed(2)) : null;
          summary.savingTotalNumeric = summary.savingWaterNumeric !== null || summary.savingSewerNumeric !== null
            ? Number((Number(summary.savingWaterNumeric || 0) + Number(summary.savingSewerNumeric || 0)).toFixed(2))
            : null;
        }
      }

      if (summary.reference && summary.reference.mode) {
        summary.metaEconomiaPercentual = this.goalCalculation && this.goalCalculation.metaEconomiaPercentual !== null
          ? this.goalCalculation.metaEconomiaPercentual
          : null;
      }

      return summary;
    },

    buildBaselineComparisonGroups(timeline) {
      const grouped = {};
      const list = Array.isArray(timeline) ? timeline : [];

      list.forEach((item) => {
        const reference = item.referencia || null;
        const configId = reference && reference.configId ? reference.configId : null;
        if (!configId) return;

        if (!grouped[configId]) {
          grouped[configId] = {
            configId,
            mode: reference.mode || null,
            validFrom: reference.validFrom || null,
            validTo: reference.validTo || null,
            metaTotal: 0,
            consumptionTotal: 0,
            pointsCount: 0,
          };
        }

        grouped[configId].metaTotal += Number(item.metaTotal || 0);
        grouped[configId].consumptionTotal += Number(item.consumptionTotal || 0);
        grouped[configId].pointsCount += 1;
      });

      return Object.values(grouped)
        .map((group) => ({
          ...group,
          metaTotal: Number(group.metaTotal.toFixed(2)),
          consumptionTotal: Number(group.consumptionTotal.toFixed(2)),
        }))
        .sort((a, b) => {
          const dateA = a.validFrom ? new Date(a.validFrom).getTime() : 0;
          const dateB = b.validFrom ? new Date(b.validFrom).getTime() : 0;
          return dateB - dateA;
        });
    },

    buildConsumptionChartPayload(timeline) {
      const points = Array.isArray(timeline) ? timeline : [];
      const labels = points.map((item) => this.formatTimelineLabel(item.date, this.chartGranularity));

      const waterBar = {
        label: "Água",
        data: points.map((item) => item.consumptionWater !== null && item.consumptionWater !== undefined ? this.scaleVolumeValue(item.consumptionWater) : null),
        backgroundColor: points.map((item) => item.isEstimative ? "#7FD9D1" : "#00897B"),
        borderColor: points.map((item) => item.isEstimative ? "#7FD9D1" : "#00897B"),
        borderWidth: 1,
        borderSkipped: false,
        order: 10,
      };

      const sewerBar = {
        label: "Efluentes",
        data: points.map((item) => item.consumptionSewer !== null && item.consumptionSewer !== undefined ? this.scaleVolumeValue(item.consumptionSewer) : null),
        backgroundColor: points.map((item) => item.isEstimative ? "#F6B37A" : "#D96B1D"),
        borderColor: points.map((item) => item.isEstimative ? "#F6B37A" : "#D96B1D"),
        borderWidth: 1,
        borderSkipped: false,
        order: 10,
      };

      const datasetsByMode = {
        total: [waterBar, sewerBar],
        water: [waterBar],
        sewer: [sewerBar],
      };

      return {
        labels,
        dataset: datasetsByMode[this.metricViewMode] || datasetsByMode.total,
      };
    },

    buildGoalChartPayload(timeline) {
      const points = Array.isArray(timeline) ? timeline : [];
      const labels = points.map((item) => this.formatTimelineLabel(item.date, this.chartGranularity));

      const waterLine = {
        label: "Meta de água",
        data: points.map((item) => item.metaAgua !== null && item.metaAgua !== undefined ? this.scaleVolumeValue(item.metaAgua) : null),
        type: "line",
        borderColor: "#005B52",
        backgroundColor: "rgba(0, 91, 82, 0.14)",
        pointBackgroundColor: "#ffffff",
        pointBorderColor: "#005B52",
        pointBorderWidth: 2,
        borderWidth: 4,
        fill: false,
        pointRadius: 3,
        pointHoverRadius: 5,
        tension: 0.2,
        order: 0,
      };

      const sewerLine = {
        label: "Meta de efluentes",
        data: points.map((item) => item.metaEsgoto !== null && item.metaEsgoto !== undefined ? this.scaleVolumeValue(item.metaEsgoto) : null),
        type: "line",
        borderColor: "#9C4310",
        backgroundColor: "rgba(156, 67, 16, 0.14)",
        pointBackgroundColor: "#ffffff",
        pointBorderColor: "#9C4310",
        pointBorderWidth: 2,
        borderWidth: 4,
        fill: false,
        pointRadius: 3,
        pointHoverRadius: 5,
        tension: 0.2,
        order: 0,
      };

      const datasetsByMode = {
        total: [waterLine, sewerLine],
        water: [waterLine],
        sewer: [sewerLine],
      };

      return {
        labels,
        dataset: datasetsByMode[this.metricViewMode] || datasetsByMode.total,
      };
    },

    buildCombinedChartPayload(timeline) {
      const consumptionPayload = this.buildConsumptionChartPayload(timeline);
      const goalPayload = this.buildGoalChartPayload(timeline);

      return {
        labels: consumptionPayload.labels,
        dataset: [
          ...(consumptionPayload.dataset || []),
          ...(goalPayload.dataset || []),
        ],
      };
    },

    scrollToMonthlyTable() {
      this.$nextTick(() => {
        const target = this.$refs.monthlyBaselineTable;
        const element = target && target.$el ? target.$el : target;
        if (element && typeof element.scrollIntoView === "function") {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    },

    getFriendlyErrorMessage(error) {
      const backendMessage = error && error.response && error.response.data
        ? error.response.data.message || ""
        : (error && error.message ? error.message : "");

      const normalizedMessage = backendMessage
        ? backendMessage.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
        : "";

      const messages = {
        "unitId e obrigatorio.": "Selecione uma unidade para consultar metas.",
        "periodDate e obrigatorio.": "Selecione uma data válida para consultar a meta.",
        "periodDate invalido.": "A data enviada para o cálculo é inválida.",
        "dateFrom e obrigatorio.": "A data inicial do período é obrigatória.",
        "dateTo e obrigatorio.": "A data final do período é obrigatória.",
        "Intervalo de datas invalido.": "O intervalo selecionado é inválido.",
        "referenceDate invalido.": "A data de referência enviada para a meta é inválida.",
        "Unidade nao possui baseline valida para a data informada.": "Não existe baseline válida para a data informada.",
        "Baseline valida nao possui dados suficientes para calcular a meta.": "A baseline válida não possui dados suficientes para calcular a meta.",
        "Unidade nao possui baselines configuradas.": "Esta unidade ainda não possui histórico de baselines configurado.",
        "Unidade nao possui baseline valida para a data de referencia.": "Não existe baseline válida para a data de referência.",
        "Usuario nao tem acesso a esta unidade.": "Você não tem acesso a esta unidade.",
      };

      return messages[normalizedMessage] || backendMessage || "Não foi possível carregar os dados de metas da unidade.";
    },

    async loadGoalCalculation() {
      this.loadingGoal = true;

      try {
        const driverActualValue = this.parseDriverActualValue();
        const response = await UnitsService.calculateUnitGoal(this.selectedUnit.id, {
          periodDate: this.referenceDatePayload,
          driverActualValue,
        });

        return response && response.data ? response.data.data || null : null;
      } finally {
        this.loadingGoal = false;
      }
    },

    async loadCurrentBaselineConfig() {
      const response = await UnitsService.getCurrentBaselineConfig(this.selectedUnit.id);
      return response && response.data ? response.data.data || null : null;
    },

    getBaselinePresetRange(baselineConfig) {
      if (!baselineConfig) {
        return { dateFrom: "", dateTo: "" };
      }

      const dateFrom = this.toMonthInputValue(baselineConfig.validFrom);
      const dateTo = this.toMonthInputValue(baselineConfig.validTo) || this.getEndOfCurrentMonth();
      return { dateFrom, dateTo };
    },

    getEndOfCurrentMonth() {
      const now = new Date();
      return this.toMonthInputValue(now);
    },

    async loadBaselineAndData(options = {}) {
      const requestId = Date.now();
      this.latestRequestId = requestId;

      if (this.hasMultipleUnitsSelected || !this.selectedUnit || !this.selectedUnit.id) {
        this.currentBaselineConfig = null;
        this.goalCalculation = null;
        this.goalTimeseriesCurrent = null;
        this.consumptionSeries = [];
        this.localDateFrom = "";
        this.localDateTo = "";
        this.errorMessage = this.validateBeforeRequest();
        return;
      }

      this.loadingGoal = true;
      this.errorMessage = "";

      try {
        const baselineConfig = await this.loadCurrentBaselineConfig();
        if (this.latestRequestId !== requestId) return;

        this.currentBaselineConfig = baselineConfig;
        const presetRange = this.getBaselinePresetRange(baselineConfig);

        this.isInitializingPeriod = true;
        this.localDateFrom = presetRange.dateFrom;
        this.localDateTo = presetRange.dateTo;
        this.isInitializingPeriod = false;

        if (!baselineConfig) {
          this.goalCalculation = null;
          this.goalTimeseriesCurrent = null;
          this.consumptionSeries = [];
          this.errorMessage = "Esta unidade ainda não possui baseline atual configurada.";
          return;
        }

        await this.loadAllData(options, requestId);
      } catch (error) {
        if (this.latestRequestId !== requestId) return;
        const message = this.getFriendlyErrorMessage(error);
        this.errorMessage = message;
        if (!options.silent) showErrorSnackBar(message);
      } finally {
        if (this.latestRequestId === requestId) {
          this.loadingGoal = false;
          this.isInitializingPeriod = false;
        }
      }
    },

    async loadGoalTimeseries() {
      this.loadingTimeseriesCurrent = true;

      try {
        const driverActualValue = this.parseDriverActualValue();
        const response = await UnitsService.calculateUnitGoalTimeseries(this.selectedUnit.id, {
          dateFrom: this.dateRangePayload.dateFrom,
          dateTo: this.dateRangePayload.dateTo,
          granularity: this.chartGranularity,
          viewMode: "current",
          referenceDate: this.referenceDatePayload,
          driverActualValue,
        });

        return response && response.data ? response.data.data || null : null;
      } finally {
        this.loadingTimeseriesCurrent = false;
      }
    },

    async loadConsumptionSeries() {
      this.loadingConsumption = true;

      try {
        const historicsResponse = await UnitsService.getSubCollectionHistorics(this.selectedUnit.id);
        const historics = historicsResponse && historicsResponse.data ? historicsResponse.data.data || [] : [];
        const historicSeries = this.normalizeHistoricSeries(historics);

        if (!this.shouldLoadCurrentMonthPlatformConsumption()) {
          return historicSeries;
        }

        const currentMonth = this.getCurrentMonthKey();
        const response = await ConsumptionService.consumptionByUnitByTariff({
          unit_id: this.selectedUnit.id,
          start_date: this.getMonthStartPayload(currentMonth),
          end_date: this.getMonthEndPayload(currentMonth),
          mode: this.chartGranularity,
          unit_measurement: "cubic_meter",
          includeInvoicedOnly: true,
        });
        const platformSeries = this.normalizePlatformConsumptionSeries(response);

        return this.mergeConsumptionSources(historicSeries, platformSeries);
      } finally {
        this.loadingConsumption = false;
      }
    },

    async loadAllData(options = {}, existingRequestId = null) {
      const requestId = existingRequestId || Date.now();
      this.latestRequestId = requestId;

      const validationMessage = this.validateBeforeRequest();
      if (validationMessage) {
        this.goalCalculation = null;
        this.goalTimeseriesCurrent = null;
        this.consumptionSeries = [];
        this.errorMessage = validationMessage;
        return;
      }

      this.errorMessage = "";

      const [goalResult, currentResult, consumptionResult] = await Promise.allSettled([
        this.loadGoalCalculation(),
        this.loadGoalTimeseries(),
        this.loadConsumptionSeries(),
      ]);

      if (this.latestRequestId !== requestId) {
        return;
      }

      this.goalCalculation = goalResult.status === "fulfilled" ? goalResult.value : null;
      this.goalTimeseriesCurrent = currentResult.status === "fulfilled" ? currentResult.value : null;
      this.consumptionSeries = consumptionResult.status === "fulfilled" ? consumptionResult.value : [];
      const activeFailedResult = currentResult;
      const activeTimeseries = this.goalTimeseriesCurrent;
      const hasActiveTimeline = activeTimeseries
        && Array.isArray(activeTimeseries.points)
        && activeTimeseries.points.length > 0;
      const hasConsumptionTimeline = Array.isArray(this.consumptionSeries) && this.consumptionSeries.length > 0;

      if (!this.goalCalculation && !this.goalTimeseriesCurrent && !hasConsumptionTimeline) {
        this.errorMessage = "Não foi possível ler os dados retornados pelo servidor.";
        return;
      }

      if (!hasActiveTimeline && activeFailedResult.status === "rejected") {
        const message = this.getFriendlyErrorMessage(activeFailedResult.reason);
        this.errorMessage = message;
        if (!options.silent) showErrorSnackBar(message);
        return;
      }

      if (goalResult.status === "rejected" && !this.goalCalculation && !hasActiveTimeline) {
        const message = this.getFriendlyErrorMessage(goalResult.reason);
        this.errorMessage = message;
        if (!options.silent) showErrorSnackBar(message);
        return;
      }

      this.errorMessage = "";
    },
  },
};
